import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Clock, 
  Users, 
  Award, 
  ChevronDown, 
  Check,
  Moon,
  Building2,
  CalendarDays,
  Star,
  MapPin,
  Phone,
  ArrowUpRight
} from 'lucide-react';
import { SEO } from '../components/SEO';
import { ServiceCard } from '../components/ServiceCard';
// TestimonialCard not used - using custom slider instead
import { services, testimonials } from '../data/content';

// Hook for reveal animation on scroll
function useReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.reveal');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

export function Home() {
  useReveal();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  
  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEO 
        title="Premium Empfangsdienstleistungen für Hotellerie"
        path="/"
      />

      {/* Hero Section with Background Image */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        aria-label="Hero Bereich"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/hero.jpeg"
            alt=""
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            fetchPriority="high"
            loading="eager"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-brand-950/80 via-brand-950/70 to-brand-950/90" />
        </div>

        {/* Content */}
        <div className="container-custom relative z-10 pt-32 pb-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="reveal inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-md rounded-full text-sm font-medium text-platin-200 border border-platin-300/30 mb-8">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              Jetzt verfügbar für neue Projekte
            </div>
            
            {/* Headline */}
            <h1 className="reveal heading-xl text-white mb-8" style={{ transitionDelay: '100ms' }}>
              Excellence in{' '}
              <span className="text-platin-200">Hospitality</span>
            </h1>
            
            {/* Subheadline */}
            <p className="reveal text-xl md:text-2xl text-platin-200/90 max-w-2xl mx-auto leading-relaxed mb-12" style={{ transitionDelay: '200ms' }}>
              ConciergeUnit bietet erstklassigen Night-Auditor-Service, Receptionsservice 
              und Event-Support. Professionell. Diskret. Zuverlässig.
            </p>

            {/* CTA Buttons */}
            <div className="reveal flex flex-col sm:flex-row gap-4 justify-center mb-16" style={{ transitionDelay: '300ms' }}>
              <Link
                to="/kontakt"
                className="group inline-flex items-center justify-center gap-3 bg-platin-200 text-brand-950 px-10 py-5 rounded-lg font-semibold 
                         hover:bg-white transition-all duration-300 hover:shadow-glow"
              >
                Anfrage stellen
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/leistungen"
                className="inline-flex items-center justify-center gap-2 border-2 border-platin-300/50 text-white px-10 py-5 rounded-lg font-semibold 
                         hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
              >
                Leistungen entdecken
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="reveal flex flex-wrap justify-center gap-8 md:gap-16" style={{ transitionDelay: '400ms' }}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center border border-platin-300/30">
                  <Clock className="w-5 h-5 text-platin-200" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-white text-lg">24/7</p>
                  <p className="text-sm text-platin-300">Verfügbarkeit</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center border border-platin-300/30">
                  <Users className="w-5 h-5 text-platin-200" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-white text-lg">10+</p>
                  <p className="text-sm text-platin-300">Jahre Erfahrung</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center border border-platin-300/30">
                  <Award className="w-5 h-5 text-platin-200" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-white text-lg">Premium</p>
                  <p className="text-sm text-platin-300">Qualität</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={() => scrollToSection('services')}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-platin-300 hover:text-white transition-colors z-10"
          aria-label="Scroll down"
        >
          <ChevronDown className="w-8 h-8 animate-bounce" />
        </button>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-brand-600 font-semibold text-sm uppercase tracking-widest">
              Unsere Leistungen
            </span>
            <h2 className="heading-lg text-brand-950 mt-4 mb-6">
              Maßgeschneiderte Lösungen für Ihren Erfolg
            </h2>
            <p className="text-body">
              Von der nächtlichen Abrechnung bis zum Event-Support – wir bieten 
              professionelle Dienstleistungen, die Ihren Betrieb reibungslos laufen lassen.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard 
                key={service.id} 
                service={service} 
                featured={index === 0}
              />
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/leistungen"
              className="inline-flex items-center gap-3 text-brand-950 font-semibold hover:text-brand-700 transition-colors group"
            >
              Alle Leistungen im Detail
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="section-padding bg-brand-950">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-platin-400 font-semibold text-sm uppercase tracking-widest">
                Eindrücke
              </span>
              <h2 className="heading-lg text-white mt-4 mb-6">
                Professionell. Diskret. Exzellent.
              </h2>
              <p className="text-platin-200 text-lg mb-8 leading-relaxed">
                Unser Team arbeitet in den renommiertesten Hotels und bei den 
                exklusivsten Events. Höchste Professionalität ist unser Standard.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Check className="w-6 h-6 text-platin-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Erfahren</h4>
                    <p className="text-platin-300 text-sm">Über 500 erfolgreiche Einsätze</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Star className="w-6 h-6 text-platin-300" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Flexibel</h4>
                    <p className="text-platin-300 text-sm">Kurzfristig verfügbar</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Gallery Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
                  <img
                    src="/images/eindrucke1.jpeg"
                    alt="Professionelle Hotelrezeption"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    width="600"
                    height="750"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.parentElement!.classList.add('bg-brand-800', 'flex', 'items-center', 'justify-center');
                      target.style.display = 'none';
                    }}
                  />
                  <div className="hidden text-platin-300">
                    <Building2 className="w-12 h-12" />
                  </div>
                </div>
                <div className="relative rounded-2xl overflow-hidden aspect-square">
                  <img
                    src="/images/eindrucke2.jpeg"
                    alt="Night Audit Service"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    width="600"
                    height="600"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.parentElement!.classList.add('bg-brand-800', 'flex', 'items-center', 'justify-center');
                      target.style.display = 'none';
                    }}
                  />
                  <div className="hidden text-platin-300">
                    <Moon className="w-12 h-12" />
                  </div>
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="relative rounded-2xl overflow-hidden aspect-square">
                  <img
                    src="/images/eindrucke3.jpeg"
                    alt="Event Betreuung"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    width="600"
                    height="600"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.parentElement!.classList.add('bg-brand-800', 'flex', 'items-center', 'justify-center');
                      target.style.display = 'none';
                    }}
                  />
                  <div className="hidden text-platin-300">
                    <CalendarDays className="w-12 h-12" />
                  </div>
                </div>
                <div className="relative rounded-2xl overflow-hidden aspect-[4/5]">
                  <img
                    src="/images/eindrucke%204.jpeg"
                    alt="Team bei der Arbeit"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    width="600"
                    height="750"
                    loading="lazy"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.parentElement!.classList.add('bg-brand-800', 'flex', 'items-center', 'justify-center');
                      target.style.display = 'none';
                    }}
                  />
                  <div className="hidden text-platin-300">
                    <Star className="w-12 h-12" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-platin-50">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-brand-950 mb-2">500+</p>
              <p className="text-gray-600">Erfolgreiche Einsätze</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-brand-950 mb-2">50+</p>
              <p className="text-gray-600">Zufriedene Partner</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-brand-950 mb-2">99%</p>
              <p className="text-gray-600">Weiterempfehlung</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-brand-950 mb-2">24/7</p>
              <p className="text-gray-600">Rund um die Uhr</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-brand-600 font-semibold text-sm uppercase tracking-widest">
              Warum ConciergeUnit
            </span>
            <h2 className="heading-lg text-brand-950 mt-4 mb-6">
              Ihr Vorteil auf einen Blick
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Clock, title: 'Sofort verfügbar', desc: 'Kurzfristige Termine und flexible Einsatzplanung' },
              { icon: Award, title: 'Zertifiziert', desc: 'Ausgebildet in allen gängigen Hotelsystemen' },
              { icon: MapPin, title: 'Vor Ort', desc: 'Einsatz in Düsseldorf und bundesweit' },
              { icon: Phone, title: 'Direkter Kontakt', desc: 'Persönliche Betreuung durch den Geschäftsführer' },
            ].map((item, index) => (
              <div key={index} className="card-luxury p-8 text-center group">
                <div className="w-16 h-16 rounded-2xl bg-brand-950 flex items-center justify-center mx-auto mb-6 group-hover:bg-platin-200 transition-colors duration-300">
                  <item.icon className="w-8 h-8 text-platin-200 group-hover:text-brand-950 transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-brand-950 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <section className="section-padding bg-brand-950">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-platin-400 font-semibold text-sm uppercase tracking-widest">
              Kundenstimmen
            </span>
            <h2 className="heading-lg text-white mt-4">
              Das sagen unsere Partner
            </h2>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-4">
                    <div className="glass-dark rounded-2xl p-8 md:p-12 text-center">
                      <Star className="w-10 h-10 text-platin-400 mx-auto mb-6" />
                      <blockquote className="text-xl md:text-2xl text-white leading-relaxed">
                        "{testimonial.text}"
                      </blockquote>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-platin-400 focus-visible:ring-offset-2 ${
                    currentTestimonial === index ? 'bg-platin-200' : 'bg-white/30'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                  aria-current={currentTestimonial === index ? 'true' : undefined}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-950 to-brand-900" />
          <div className="absolute inset-0 opacity-20">
            <img
              src="/images/cta-bg.jpg"
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
          </div>
        </div>

        <div className="container-custom relative z-10 text-center">
          <h2 className="heading-lg text-white mb-6">
            Bereit für Excellence?
          </h2>
          <p className="text-platin-200 text-lg max-w-2xl mx-auto mb-10">
            Lassen Sie uns gemeinsam Ihre Gäste begeistern. Kontaktieren Sie uns 
            für ein unverbindliches Gespräch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              className="group inline-flex items-center justify-center gap-3 bg-platin-200 text-brand-950 px-10 py-5 rounded-lg font-semibold 
                       hover:bg-white transition-all duration-300 hover:shadow-glow"
            >
              Kontakt aufnehmen
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/jobs"
              className="inline-flex items-center justify-center gap-2 border-2 border-platin-300/50 text-white px-10 py-5 rounded-lg font-semibold 
                       hover:bg-white/10 transition-all duration-300"
            >
              Karriere bei CU
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
